import { Select } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { TYPE_LABELS } from '@/constants'
import type { UnitOfMeasurementInterface } from '@/types'
import { fetchUnitsOfMeasurement } from '@/utils/api/fetch/fetchEntities'
import { getStatusBadge } from '@/utils/catalog/approvalStatus'

import type { AvailableCharacteristic } from '../../../../AttributeProcessing.types'
import cn from '../CreationDictionaryTables.module.scss'

type CreationCharacteristicsTableProps = {
  data: AvailableCharacteristic[]
  loading: boolean
  onUnitChange: (
    characteristic: AvailableCharacteristic,
    nextUnitId: string
  ) => Promise<void>
  selectedCharacteristicId?: string
  onSelectCharacteristic: (characteristic: AvailableCharacteristic) => void
}

function CreationCharacteristicsTable(
  props: CreationCharacteristicsTableProps
) {
  const { data, loading, onUnitChange } = props
  const { selectedCharacteristicId, onSelectCharacteristic } = props
  const [searchValue, setSearchValue] = useState('')
  const [units, setUnits] = useState<UnitOfMeasurementInterface[]>([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [savingId, setSavingId] = useState('')
  const [draftUnits, setDraftUnits] = useState<Record<string, string>>({})

  useEffect(() => {
    let isActive = true

    const loadUnits = async () => {
      setUnitsLoading(true)
      try {
        const fetchedUnits = await fetchUnitsOfMeasurement()
        if (isActive) setUnits(fetchedUnits)
      } catch {
        toast.error('Не удалось загрузить единицы измерения')
      } finally {
        if (isActive) setUnitsLoading(false)
      }
    }

    void loadUnits()

    return () => {
      isActive = false
    }
  }, [])

  const filteredData = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase()
    if (!normalizedSearch) return data

    return data.filter((characteristic) => {
      const typeLabel = TYPE_LABELS[characteristic.type] ?? characteristic.type
      const statusLabel = characteristic.status ?? 'APPROVED'

      return [
        String(characteristic.code),
        characteristic.name,
        characteristic.unit ?? '',
        typeLabel,
        statusLabel,
      ].some((value) => value.toLowerCase().includes(normalizedSearch))
    })
  }, [data, searchValue])

  const resolveUnitId = (characteristic: AvailableCharacteristic) =>
    draftUnits[characteristic.id] ?? characteristic.unitId ?? ''

  const handleUnitSelect = async (
    characteristic: AvailableCharacteristic,
    nextUnitId?: string
  ) => {
    const normalizedUnitId = nextUnitId ?? ''
    setDraftUnits((prevState) => ({
      ...prevState,
      [characteristic.id]: normalizedUnitId,
    }))

    if (normalizedUnitId === (characteristic.unitId ?? '')) return

    setSavingId(characteristic.id)

    try {
      await onUnitChange(characteristic, normalizedUnitId)
      setDraftUnits((prevState) => {
        const nextState = { ...prevState }
        delete nextState[characteristic.id]
        return nextState
      })
      toast.success('Единица измерения обновлена')
    } finally {
      setSavingId('')
    }
  }

  return (
    <div className={cn.block}>
      <div className={cn.toolbar}>
        <div className={cn.toolbarTitle}>Существующие характеристики</div>
        <input
          value={searchValue}
          className={cn.searchInput}
          placeholder="Поиск по коду, названию, типу..."
          onChange={(event) => setSearchValue(event.target.value)}
        />
      </div>

      <div className={cn.tableViewport}>
        <table className={[cn.table, cn.characteristicsTable].join(' ')}>
          <thead>
            <tr>
              <th className={cn.codeCol}>Код</th>
              <th>Наименование</th>
              <th className={cn.unitCol}>Ед. изм.</th>
              <th className={cn.typeCol}>Тип</th>
              <th className={cn.statusCol}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {loading && filteredData.length === 0 ? (
              <tr>
                <td className={cn.loadingCell} colSpan={5}>
                  <Loader size={18} />
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((characteristic) => (
                <tr
                  key={characteristic.id}
                  className={
                    selectedCharacteristicId === characteristic.id
                      ? cn.rowSelected
                      : ''
                  }
                  onClick={() => onSelectCharacteristic(characteristic)}
                >
                  <td>{characteristic.code}</td>
                  <td className={cn.nameCell}>{characteristic.name}</td>
                  <td>
                    {characteristic.type === 'BOOLEAN' ? (
                      '—'
                    ) : (
                      <Select
                        allowClear
                        showSearch
                        size="small"
                        value={resolveUnitId(characteristic) || undefined}
                        className={cn.unitSelect}
                        disabled={
                          unitsLoading || savingId === characteristic.id
                        }
                        optionFilterProp="label"
                        options={units.map((unit) => ({
                          value: unit.id,
                          label: unit.description,
                        }))}
                        placeholder="Без ед. изм."
                        onClick={(event) => event.stopPropagation()}
                        onChange={(value) =>
                          void handleUnitSelect(characteristic, value)
                        }
                      />
                    )}
                  </td>
                  <td>
                    {TYPE_LABELS[characteristic.type] ?? characteristic.type}
                  </td>
                  <td>
                    {getStatusBadge(characteristic.status) ?? 'Утверждена'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className={cn.emptyCell} colSpan={5}>
                  Характеристики не найдены
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CreationCharacteristicsTable
